import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Copy, Check, Link2, AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useRegionFilter } from "@/features/regions/hooks/use-region-filter";
import { getDashboardBaseUrl } from "@/lib/dashboard-url";
import {
  verifyUsageId,
  type VerifyUsageIdResult,
} from "@/features/users/services/verify-usage-id";
import {
  getEventTypes,
  type EventType,
} from "@/features/supply-config/event-types-api";
import DecoratorProfessionalPicker from "@/features/users/ui/decorator-professional-picker";
import DecoratorRoundRobinPicker from "@/features/users/ui/decorator-roundrobin-picker";

type LinkType = "individual" | "round_robin";

// Bake usage_id (+ user_id) into the raw Calendly scheduling URL as utm_content,
// the convention the booking webhook reads to auto-link + assign professional.
// Any pre-existing utm_content is dropped first — a duplicate makes Calendly send
// utm_content as an array, which the webhook rejects.
function buildCalendlyUrl(
  schedulingUrl: string,
  usageId: string,
  userId: string | null,
): string {
  try {
    const u = new URL(schedulingUrl);
    u.searchParams.delete("utm_content");
    u.searchParams.set(
      "utm_content",
      JSON.stringify({
        usage_id: usageId,
        ...(userId ? { user_id: userId } : {}),
      }),
    );
    return u.toString();
  } catch {
    return schedulingUrl;
  }
}

export default function DecoratorLinks() {
  const { toast } = useToast();
  const region = useRegionFilter();

  const [usageIdInput, setUsageIdInput] = useState("");
  const [verified, setVerified] = useState<VerifyUsageIdResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const [linkType, setLinkType] = useState<LinkType>("individual");
  const [selectedEventTypeId, setSelectedEventTypeId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const usage = verified?.usage ?? null;
  const managedEventIds = usage?.managed_event_ids ?? [];
  const userId = usage?.users?.[0]?.id ?? null;

  const handleVerify = async () => {
    const trimmed = usageIdInput.trim();
    if (!trimmed) return;
    setIsVerifying(true);
    setVerifyError(null);
    setSelectedEventTypeId(null);
    try {
      const res = await verifyUsageId(trimmed);
      if (!res.success || !res.usage) {
        setVerified(null);
        setVerifyError(res.message || "Pack does not exist.");
      } else {
        setVerified(res);
      }
    } catch {
      setVerified(null);
      setVerifyError("Failed to verify usage ID. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Event types scoped to THIS bundle's managed events + chosen type.
  const {
    data: eventTypes = [],
    isFetching: isLoadingEventTypes,
  } = useQuery({
    queryKey: ["decorator-event-types", usage?.id, linkType, managedEventIds],
    enabled: !!usage && managedEventIds.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        managedEventIds.map((id) =>
          getEventTypes({ managed_event_id: id, type: linkType, limit: 1000 }),
        ),
      );
      const merged = results.flatMap((r) => r.eventTypes);
      // Dedupe by id, keep only ones with a usable scheduling URL.
      const seen = new Set<string>();
      return merged.filter((et) => {
        if (!et.scheduling_url || seen.has(et.id)) return false;
        seen.add(et.id);
        return true;
      });
    },
  });

  const selectedEventType = useMemo(
    () => eventTypes.find((et) => et.id === selectedEventTypeId) ?? null,
    [eventTypes, selectedEventTypeId],
  );

  const decoratorLink = useMemo(() => {
    if (!usage || !selectedEventType?.scheduling_url) return null;
    const calendly = buildCalendlyUrl(
      selectedEventType.scheduling_url,
      usage.id,
      userId,
    );
    const params = new URLSearchParams();
    params.set("calendly", calendly);
    params.set("usage_id", usage.id);
    // Lets the dashboard schedule page fetch this event's TnC (schedulingTitle /
    // schedulingDescription) instead of the static config text.
    if (selectedEventType.managed_event_id) {
      params.set("managed_event_id", selectedEventType.managed_event_id);
    }
    if (region) params.set("region", region);
    return `${getDashboardBaseUrl()}/schedule/subscriptionTherapy?${params.toString()}`;
  }, [usage, selectedEventType, userId, region]);

  const handleCopy = async () => {
    if (!decoratorLink) return;
    try {
      await navigator.clipboard.writeText(decoratorLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copied", description: "Decorator link copied to clipboard." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the link and copy it manually.",
        variant: "destructive",
      });
    }
  };

  const switchType = (t: LinkType) => {
    setLinkType(t);
    setSelectedEventTypeId(null);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Link2 className="h-6 w-6" />
          Decorator Links
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Build a scheduling link that auto-links the booking to a bundle usage — no
          manual usage-ID mapping afterward.
        </p>
      </div>

      {/* Step 1: Usage ID */}
      <Card className="p-4 space-y-3">
        <Label htmlFor="usage-id">1. Bundle Usage ID</Label>
        <div className="flex gap-2">
          <Input
            id="usage-id"
            placeholder="Enter bundle usage ID"
            value={usageIdInput}
            onChange={(e) => setUsageIdInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            className="font-mono"
          />
          <Button onClick={handleVerify} disabled={isVerifying || !usageIdInput.trim()}>
            {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </Button>
        </div>

        {verifyError && (
          <div className="flex items-start gap-2 text-sm text-red-700 rounded-md border border-red-200 bg-red-50 p-3">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            {verifyError}
          </div>
        )}

        {usage && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-1 text-sm">
            <div className="flex items-center gap-2 text-green-800 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              {usage.bundle_name ?? "Bundle"}
              <Badge className="bg-white text-gray-700 border-gray-200">
                {usage.status}
              </Badge>
            </div>
            <div className="text-gray-600">
              Product: {usage.product_name ?? "—"}
            </div>
            <div className="text-gray-600">
              User:{" "}
              {usage.users.length
                ? usage.users.map((u) => u.full_name).join(", ")
                : "⚠ no user mapped"}
            </div>
            {managedEventIds.length === 0 && (
              <div className="text-yellow-700">
                ⚠ This bundle has no configured events (session_types) — no links can
                be built.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Step 2 + 3: type + event */}
      {usage && managedEventIds.length > 0 && (
        <Card className="p-4 space-y-4">
          <div className="space-y-2">
            <Label>2. Session type</Label>
            <div className="flex gap-2">
              <Button
                variant={linkType === "individual" ? "default" : "outline"}
                onClick={() => switchType("individual")}
              >
                Individual
              </Button>
              <Button
                variant={linkType === "round_robin" ? "default" : "outline"}
                onClick={() => switchType("round_robin")}
              >
                Round-robin
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              3. {linkType === "individual" ? "Professional" : "Round-robin team"}
            </Label>
            {isLoadingEventTypes ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
              </div>
            ) : eventTypes.length === 0 ? (
              <p className="text-sm text-gray-500">
                No {linkType === "individual" ? "individual" : "round-robin"} events
                configured for this bundle.
              </p>
            ) : linkType === "individual" ? (
              <DecoratorProfessionalPicker
                eventTypes={eventTypes}
                value={selectedEventTypeId}
                onChange={setSelectedEventTypeId}
              />
            ) : (
              <DecoratorRoundRobinPicker
                eventTypes={eventTypes}
                value={selectedEventTypeId}
                onChange={setSelectedEventTypeId}
              />
            )}
          </div>
        </Card>
      )}

      {/* Result */}
      {decoratorLink && (
        <Card className="p-4 space-y-2">
          <Label>Decorator link</Label>
          <div className="flex gap-2">
            <Input readOnly value={decoratorLink} className="font-mono text-xs" />
            <Button variant="outline" onClick={handleCopy}>
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Send this to the patient. On booking, the session auto-links to the bundle
            and the professional is updated.
          </p>
        </Card>
      )}
    </div>
  );
}
